import {
  Cached,
  ClearOutlined,
  ClearAllOutlined,
  DateRangeOutlined,
  FilterAltOutlined,
  KeyboardDoubleArrowRight,
  PersonOutlined,
  SearchOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { ptBR } from "@mui/x-data-grid/locales";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Avatar,
  AvatarGroup,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  linearProgressClasses,
  Paper,
  Snackbar,
  Tab,
  TablePagination,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import api from "../../../services/api";

import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { StatusChipModal } from "../../../utils/chipsErp/chipStatusModal";
import VirtualizedAutocomplete from "../../../components/virtualization/virtualizedAutocomplete";
import { renderOpcaoCliente } from "../../../components/opcaoCliente";
import useStyles from "./styles";
import TicketModalContent from "../../../components/tickets/detailsTicketComponent";
import TicketModalContentIxc from "../../../components/tickets/detailsTicketIxcComponent";
import InternoModalConfirmacaoSaida from "../../../components/modalSaindoDaPagina";
import FilterBar from "../../../components/topBarFilter";
import ButtonTime from "../../../components/buttonTime";
import CardDataComponent from "../../../components/overview/cardDataComponent";
import DatagridComponent from "../../../components/datagridComponent";
import useErpSelecionado from "../../../components/erp/useErpSelecionado";
import ErpChip from "../../../components/erp/ErpChip";
import { tiposDaTela } from "../../../services/telasErp";

dayjs.locale("pt-br");

// A tela junta os atendimentos de todos os ERPs dela como se fossem um só.
// Ids se repetem entre ERPs, então todo id de ERP vira a chave
// `${erpId}|${id}`; e os filtros são unificados pelo nome (o "Pendente" da
// HubSoft e o do IXC viram uma opção só, que guarda as chaves das duas).
const chave = (erpId, id) => `${erpId ?? ""}|${id}`;
const normalizar = (texto) => String(texto || "").trim().toLowerCase();

// itens: [{ erpId, id, label, original? }] → [{ id, label, chaves, original }]
const unificar = (itens) => {
  const mapa = new Map();
  itens.forEach(({ erpId, id, label, original }) => {
    if (id == null || !label) return;
    const k = normalizar(label);
    const opcao = mapa.get(k) || { id: k, label, chaves: new Set(), original };
    opcao.chaves.add(chave(erpId, id));
    mapa.set(k, opcao);
  });
  return [...mapa.values()].sort((a, b) => a.label.localeCompare(b.label));
};

// Filtros aplicados no front (os tickets do período vêm todos do backend).
// `origem`: lista da resposta de paramsFilterWorkspace (sem ela em nenhum
// ERP, o filtro some); `id`/`nome` leem a opção; `valores` dá os ids do ticket.
const FILTROS = [
  {
    // Opções: os ERPs da tela (montadas no componente, só com mais de um).
    key: "erps",
    label: "ERP",
    chip: "ERP",
    origem: null,
    valores: (t) => [t.erpId],
  },
  {
    key: "responsaveis",
    label: "Responsáveis",
    chip: "Responsável",
    origem: "userResponsaveis",
    id: (o) => o.id,
    nome: (o) => o.name,
    valores: (t) => (t.usuario_responsavel || []).map((u) => u.id),
  },
  {
    key: "clientes",
    label: "Clientes",
    chip: "Cliente",
    origem: "clientes",
    id: (o) => o.clienteErpId,
    nome: (o) => `${o.clienteErpId} - ${o.nome || "Sem Nome"}`,
    valores: (t) => [t.cliente?.codigo_cliente],
  },
  {
    key: "status",
    label: "Status",
    chip: "Status",
    origem: "statusAtendimento",
    // Pelo id: a descrição da opção pode diferir da do ticket
    // ("Pendente (Abertura de OS)" × "Pendente").
    id: (o) => o.id,
    nome: (o) => o.descricao,
    valores: (t) => [t.status?.id],
  },
  {
    key: "tiposAtendimento",
    label: "Tipo de atendimento",
    chip: "Tipo",
    origem: "tipoAtendimento",
    id: (o) => o.id,
    nome: (o) => o.descricao,
    valores: (t) => [t.tipoAtendimento?.id],
  },
  {
    key: "etapasFluxo",
    label: "Etapa do fluxo",
    chip: "Etapa",
    origem: "etapasFluxoAtendimento",
    id: (o) => o.idEtapaFluxoAtendimento,
    nome: (o) => o.etapaFluxoAtendimento,
    valores: (t) => [t.id_fluxo_atendimento_etapa],
  },
  {
    key: "motivosFechamento",
    label: "Motivo de fechamento",
    chip: "Motivo",
    origem: "motivoFechamento",
    id: (o) => o.id,
    nome: (o) => o.descricao,
    valores: (t) => [t.motivoFechamento?.id],
  },
];

// Opções de cada ERP que não entram no filtro de status: o resolvido (a aba
// "Resolvidos" já separa).
const statusForaDoFiltro = { HUBSOFT: [3] };

// Períodos relativos do ButtonTime (o backend aceita até 6 meses).
const OPCOES_TEMPO = {
  0: { label: "Hoje", inicio: () => dayjs().startOf("day") },
  1: { label: "Últimas 24 horas", inicio: () => dayjs().subtract(24, "hour") },
  2: { label: "Últimos 7 dias", inicio: () => dayjs().subtract(7, "day") },
  3: { label: "Últimos 15 dias", inicio: () => dayjs().subtract(15, "day") },
  4: { label: "Últimos 30 dias", inicio: () => dayjs().subtract(30, "day") },
  5: { label: "Últimos 90 dias", inicio: () => dayjs().subtract(90, "day") },
  6: { label: "Últimos 6 meses", inicio: () => dayjs().subtract(6, "month") },
};
const TEMPO_PADRAO = 2;

// Relativo: as duas datas são recalculadas a partir de agora.
const periodoRelativo = (key) => ({
  selectTime: key,
  dataInicial: OPCOES_TEMPO[key].inicio().second(0).millisecond(0),
  dataFinal: dayjs().second(0).millisecond(0),
});

const INITIAL_FILTERS = {
  ...periodoRelativo(TEMPO_PADRAO),
  ...Object.fromEntries(FILTROS.map((f) => [f.key, []])),
};

// Sem filtros, mantendo o período.
const semFiltros = (filtros) => ({
  ...INITIAL_FILTERS,
  selectTime: filtros.selectTime,
  dataInicial: filtros.dataInicial,
  dataFinal: filtros.dataFinal,
});

// Os filtros guardam os nomes unificados (não ids de um ERP): chave nova.
const STORAGE_FILTROS = "filterWorkspaceErps";

const loadFiltersFromStorage = () => {
  try {
    localStorage.removeItem("filterWorkspace"); // formato antigo (por ERP)
    const salvos = JSON.parse(localStorage.getItem(STORAGE_FILTROS));
    if (!salvos) return INITIAL_FILTERS;
    return {
      ...INITIAL_FILTERS,
      ...salvos,
      // Relativo (ou salvo antes do ButtonTime): recalcula a partir de agora.
      ...(salvos.selectTime === null && salvos.dataInicial && salvos.dataFinal
        ? {
            dataInicial: dayjs(salvos.dataInicial),
            dataFinal: dayjs(salvos.dataFinal),
          }
        : periodoRelativo(
            OPCOES_TEMPO[salvos.selectTime] ? salvos.selectTime : TEMPO_PADRAO,
          )),
    };
  } catch {
    return INITIAL_FILTERS;
  }
};

// O backend marca `finalizado` em todo ERP (HubSoft: Resolvido; IXC:
// Solucionado ou Cancelado).
const isResolvido = (ticket) => !!ticket.finalizado;

const formatDate = (iso) => (iso ? dayjs(iso).format("DD/MM/YY HH:mm") : "-");

// Tempo entre abertura e fechamento (ou agora): "45min", "5h", "3d".
const formatarMinutos = (min) => {
  if (min < 60) return `${Math.max(Math.round(min), 0)}min`;
  if (min < 60 * 24) return `${Math.floor(min / 60)}h`;
  return `${Math.floor(min / 60 / 24)}d`;
};

const duracao = (inicio, fim) =>
  formatarMinutos(dayjs(fim || undefined).diff(dayjs(inicio), "minute"));

const nomeCliente = (ticket) =>
  ticket.cliente
    ? `${ticket.cliente.codigo_cliente} - ${ticket.cliente.nome || ""}`
    : "Cliente não informado";

// StatusChipModal usa hooks: chamado direto num .map(), muda a ordem dos
// hooks quando a lista muda de tamanho.
const StatusChip = ({ prefixo }) => StatusChipModal(prefixo);

function Responsaveis({ usuarios = [], sx }) {
  if (!usuarios.length)
    return (
      <Typography variant="caption" color="text.secondary">
        Sem responsável
      </Typography>
    );
  return (
    <Tooltip
      arrow
      placement="top"
      title={usuarios.map((u) => (
        <Typography key={u.id} variant="caption" display="block">
          • {u.name}
        </Typography>
      ))}
    >
      <AvatarGroup max={3} sx={sx}>
        {usuarios.map((u) => (
          <Avatar key={u.id} alt={u.name} src={u.imagem?.link} />
        ))}
      </AvatarGroup>
    </Tooltip>
  );
}

// Lista "nome ▬▬▬ total" clicável: cada linha liga/desliga o filtro do item.
function PainelRanking({ titulo, itens, selecionados, onToggle, loading }) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const max = Math.max(1, ...itens.map((i) => i.value));
  return (
    <Paper
      variant={theme.palette.mode === "dark" ? "outlined" : "elevation"}
      elevation={theme.palette.mode === "dark" ? 0 : 3}
      sx={{ ...styles.paperPanel, ...styles.paperRanking }}
    >
      {loading && <LinearProgress sx={styles.loadingLinearProgress} />}
      <Typography sx={styles.titlePanel}>{titulo}</Typography>
      <Divider sx={{ pt: 1 }} />
      <Box sx={styles.boxRanking}>
        {itens.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            Nenhum ticket aberto.
          </Typography>
        )}
        {itens.map((item) => {
          const clicavel = item.id != null;
          return (
            <Box
              key={item.id ?? "sem"}
              onClick={clicavel ? () => onToggle(item.id) : undefined}
              sx={{
                ...styles.rankingItem,
                cursor: clicavel ? "pointer" : "default",
                bgcolor: selecionados.includes(item.id)
                  ? "action.selected"
                  : "transparent",
                "&:hover": clicavel ? { bgcolor: "action.hover" } : {},
              }}
            >
              <Box sx={styles.rankingLabel}>
                <Typography variant="body2" noWrap title={item.label}>
                  {item.label}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {item.value}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(item.value / max) * 100}
                sx={{
                  ...styles.rankingBar,
                  [`&.${linearProgressClasses.colorPrimary}`]: {
                    backgroundColor: `${theme.palette.colors.gray.dark}2c`,
                  },
                  [`& .${linearProgressClasses.bar}`]: {
                    borderRadius: 5,
                    backgroundColor: item.color || theme.palette.primary.main,
                  },
                }}
              />
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

export default function WorkSpace() {
  // Todos os ERPs que têm a tela, juntos. Sem ERP cadastrado: um só, sem
  // erpId (o backend usa a variável ERP), do tipo `erp`.
  const { erps, erp, carregado } = useErpSelecionado(
    tiposDaTela("/interno/suporte/workSpace"),
  );
  const erpsDaTela = erps?.length ? erps : [{ id: null, tipo: erp, nome: erp }];
  // Coluna e filtro de ERP só fazem sentido com mais de um.
  const variosErps = erpsDaTela.length > 1;
  const erpDoTicket = (t) =>
    erpsDaTela.find((item) => item.id === t.erpId) || { tipo: t.erpTipo };

  const theme = useTheme();
  const styles = useStyles(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Usuário da HubSoft vinculado ao logado (Administração › Usuários).
  const userErpId = Number(
    JSON.parse(localStorage.getItem("user") || "{}")?.usuario?.userErpId,
  );

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTicketDetails, setLoadingTicketDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [aba, setAba] = useState("abertos");
  const [openFiltros, setOpenFiltros] = useState(false);
  const [openFilterBar, setOpenFilterBar] = useState(false);
  const [filters, setFilters] = useState(loadFiltersFromStorage);
  // Período dos tickets na tela (as datas da barra só valem ao atualizar).
  const [periodo, setPeriodo] = useState(null);
  const [pageMobile, setPageMobile] = useState(0);
  const [rowsPerPageMobile, setRowsPerPageMobile] = useState(10);

  const [editedTicket, setEditedTicket] = useState(false); //Necessário para verificar se houve edição no ticket
  // Resposta de paramsFilterWorkspace de cada ERP: [{ erp, data }].
  const [paramsPorErp, setParamsPorErp] = useState([]);
  // ERP do ticket aberto no modal (cada um abre o modal do seu ERP).
  const [ticketAberto, setTicketAberto] = useState({});
  const [itensPublic, setItensPublic] = useState({
    mensagens: [],
    anexos: [],
    rfo: [],
  });
  const colors = Object.keys(theme.palette.colors)?.filter(
    (color) => color !== "white" && color !== "black",
  );

  const [isEditOs, setIsEditOs] = useState(false);
  const [isNewOs, setIsNewOs] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [ticketDetails, setTicketDetails] = useState({});
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(0);
  const [confirmacaoSaidaPagina, setConfirmacaoSaidaPagina] = useState(false);
  const [isEditTicket, setIsEditTicket] = useState(false);
  const [isFinishTicket, setIsFinishTicket] = useState(false);
  const [allEtapasFluxoAtendimentos, setAllEtapasFluxoAtendimentos] = useState(
    [],
  );

  const fluxoAtendimentoColor = allEtapasFluxoAtendimentos.reduce(
    (acc, item, index) => {
      acc[item] = colors[index % colors.length];
      return acc;
    },
    {},
  );

  // Filtros salvos a cada mudança (datas como string ISO).
  useEffect(() => {
    localStorage.setItem(STORAGE_FILTROS, JSON.stringify(filters));
  }, [filters]);

  // Espera a lista de ERPs; relativo recalcula o período a partir de agora.
  useEffect(() => {
    if (!carregado) return;
    const atuais =
      filters.selectTime != null
        ? { ...filters, ...periodoRelativo(filters.selectTime) }
        : filters;
    setFilters(atuais);
    getParamsFilter();
    findTicketsWorkspace(atuais);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregado]);

  // Uma requisição por ERP, em paralelo. ERPs que falharem ficam de fora (e
  // avisam), sem derrubar os outros.
  const porErp = (rota, body) =>
    Promise.allSettled(
      erpsDaTela.map((item) =>
        api
          .post(rota, { ...body, erpId: item.id || undefined })
          .then(({ data }) => ({ erp: item, data })),
      ),
    ).then((resultados) => ({
      ok: resultados.filter((r) => r.status === "fulfilled").map((r) => r.value),
      falhas: erpsDaTela.filter((_, i) => resultados[i].status === "rejected"),
    }));

  const avisarFalhas = (falhas, oQue) =>
    falhas.length &&
    setSnackbar({
      children: `Não foi possível buscar ${oQue} de: ${falhas
        .map((item) => item.nome || item.tipo)
        .join(", ")}.`,
      severity: "warning",
    });

  function htmlForText(html) {
    if (!html) return "";

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    function extrairTexto(node) {
      let texto = "";

      node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          texto += child.textContent;
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          const tag = child.tagName;

          switch (tag) {
            case "BR":
              texto += "\n";
              break;
            case "P":
            case "DIV":
              texto += extrairTexto(child) + "\n";
              break;
            case "LI":
              texto += "- " + extrairTexto(child) + "\n";
              break;
            case "UL":
            case "OL":
              texto += extrairTexto(child) + "\n";
              break;
            default:
              texto += extrairTexto(child);
          }
        }
      });

      return texto;
    }

    // Extrai texto e normaliza múltiplas quebras de linha
    const resultado = extrairTexto(tempDiv);
    return resultado.replace(/\n{3,}/g, "\n\n").trim();
  }
  const handleCancelNavigation = () => {
    setConfirmacaoSaidaPagina(false);
  };

  const tipoAberto = ticketAberto.erpTipo || erp;
  const labelTabsErp = {
    descricao: tipoAberto === "VOALLE" ? "Geral" : "Descrição",
    mensagens: tipoAberto === "VOALLE" ? "Histórico" : "Mensagens",
  };

  const getParamsFilter = async () => {
    const { ok, falhas } = await porErp("/interno/find/paramsFilterWorkspace");
    setParamsPorErp(ok);
    avisarFalhas(falhas, "os filtros");
  };

  const findTicketsWorkspace = async (currentFilters) => {
    const { dataInicial, dataFinal } = currentFilters;
    if (!dataInicial?.isValid() || !dataFinal?.isValid()) {
      setSnackbar({
        children: "Informe a data inicial e a data final.",
        severity: "warning",
      });
      return;
    }
    if (dataInicial.isAfter(dataFinal)) {
      setSnackbar({
        children: "A data inicial não pode ser maior que a data final.",
        severity: "warning",
      });
      return;
    }
    if (dataFinal.isAfter(dataInicial.add(6, "month"))) {
      setSnackbar({
        children: "O período selecionado não pode ultrapassar 6 meses.",
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    const { ok, falhas } = await porErp("/interno/find/tickets/workspace", {
      dataInicial: dataInicial.format("YYYY-MM-DD HH:mm:ss"),
      dataFinal: dataFinal.format("YYYY-MM-DD HH:mm:ss"),
    });
    // Cada ticket leva o ERP de origem (modal, chaves dos filtros) e uma
    // `rowId` única entre ERPs. ponytail: a query da HubSoft pode repetir o
    // atendimento (LEFT JOIN no item do fluxo); fica a 1ª linha de cada id.
    const unicos = new Map();
    ok.forEach(({ erp: origem, data }) =>
      (data?.atendimentos || []).forEach((t) => {
        const rowId = chave(origem.id, t.id);
        if (!unicos.has(rowId))
          unicos.set(rowId, {
            ...t,
            rowId,
            erpId: origem.id,
            erpTipo: origem.tipo,
          });
      }),
    );
    setTickets(
      [...unicos.values()].sort((a, b) =>
        String(b.data_cadastro).localeCompare(String(a.data_cadastro)),
      ),
    );
    setAllEtapasFluxoAtendimentos([
      ...new Set(ok.flatMap(({ data }) => data?.allEtapasFluxoAtendimentos || [])),
    ]);
    setPeriodo({ dataInicial, dataFinal });
    setPageMobile(0);
    setLoading(false);
    if (ok.length) avisarFalhas(falhas, "os tickets");
    else
      setSnackbar({
        children: "Não foi possível buscar os tickets.",
        severity: "error",
      });
  };

  // ===== PERÍODO (ButtonTime) =====
  const [anchorTempo, setAnchorTempo] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const aplicarPeriodo = (periodoNovo) => {
    const novos = { ...filters, ...periodoNovo };
    setFilters(novos);
    setAnchorTempo(null);
    findTicketsWorkspace(novos);
  };

  const handleSelectTime = (key) => {
    setStartDate(null);
    setEndDate(null);
    aplicarPeriodo(periodoRelativo(key));
  };

  const handleAplicarTempo = () =>
    aplicarPeriodo({
      selectTime: null,
      dataInicial: dayjs(startDate),
      dataFinal: dayjs(endDate),
    });

  const isValidRange =
    !!startDate?.isValid() &&
    !!endDate?.isValid() &&
    !startDate.isAfter(endDate) &&
    !endDate.isAfter(startDate.add(6, "month"));

  // "Até" vai no máximo 6 meses depois do "De" (e nunca além de agora).
  const limiteFinal = startDate?.add(6, "month");

  // Relativo: recalcula até agora; absoluto: repete o mesmo intervalo.
  const buscar = () =>
    filters.selectTime != null
      ? handleSelectTime(filters.selectTime)
      : findTicketsWorkspace(filters);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setIsEditOs(false);
    setIsEditTicket(false);
    setIsFinishTicket(false);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEditTicket(false);
    setIsFinishTicket(false);
    setIsEditOs(false);
    setIsNewOs(false);
    setConfirmacaoSaidaPagina(false);
    setValue(0);
    if (editedTicket) {
      findTicketsWorkspace(filters);
      setEditedTicket(false);
    }
  };

  const setFiltro = (key, valor) => {
    setFilters((prev) => ({ ...prev, [key]: valor }));
    setPageMobile(0);
  };

  const toggleFiltro = (key, id) =>
    setFiltro(
      key,
      filters[key]?.includes(id)
        ? filters[key].filter((v) => v !== id)
        : [...(filters[key] || []), id],
    );

  const handleClearFilters = () => {
    setFilters(semFiltros);
    setPageMobile(0);
  };

  // O detalhe vem do ERP do próprio ticket.
  const getTicketDetails = async (ticket) => {
    try {
      setLoadingTicketDetails(true);
      const response = await api.post("/interno/find/tickets/id", {
        ticketId: ticket.id,
        erpId: ticket.erpId || undefined,
      });

      return response?.data;
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingTicketDetails(false);
    }
  };

  const handleOpen = async (ticket) => {
    setTicketAberto({ erpId: ticket.erpId, erpTipo: ticket.erpTipo });
    setTicketDetails({});
    setOpen(true);
    const response = await getTicketDetails(ticket);
    setTicketDetails(response || {});
  };

  // ===== OPÇÕES DOS FILTROS (todos os ERPs, unificadas pelo nome) =====
  // Clientes vêm completos em cada resposta: só os dos ERPs da tela, sem
  // grupo econômico (não tem código no ERP).
  const idsDaTela = erps?.length ? erpsDaTela.map((item) => item.id) : null;
  const opcoes = {};
  FILTROS.forEach((f) => {
    const listas = paramsPorErp.filter(({ data }) => data?.[f.origem]);
    if (!listas.length) return; // nenhum ERP tem: o filtro some
    opcoes[f.key] = unificar(
      listas.flatMap(({ erp: origem, data }) =>
        data[f.origem]
          .filter((o) =>
            f.key === "clientes"
              ? !o?.isGrupoEconomico &&
                (!idsDaTela || idsDaTela.includes(o?.erpId))
              : f.key !== "status" ||
                !statusForaDoFiltro[origem.tipo]?.includes(o?.id),
          )
          .map((o) => ({
            erpId: f.key === "clientes" ? (idsDaTela ? o.erpId : null) : origem.id,
            id: f.id(o),
            label: f.nome(o),
            original: o,
          })),
      ),
    );
  });
  // Responsáveis só dos tickets (ex.: funcionário inativo no IXC) também
  // entram, para o painel "Abertos por responsável" sempre filtrar.
  if (variosErps) {
    opcoes.erps = erpsDaTela.map((item) => ({
      id: item.id,
      label: item.nome || item.tipo,
      chaves: new Set([chave(item.id, item.id)]),
      original: item,
    }));
  }
  if (opcoes.responsaveis) {
    opcoes.responsaveis = unificar([
      ...opcoes.responsaveis.flatMap((o) =>
        [...o.chaves].map((k) => ({
          erpId: k.slice(0, k.indexOf("|")),
          id: k.slice(k.indexOf("|") + 1),
          label: o.label,
        })),
      ),
      ...tickets.flatMap((t) =>
        (t.usuario_responsavel || []).map((u) => ({
          erpId: t.erpId,
          id: u.id,
          label: u.name,
        })),
      ),
    ]);
  }

  // Chaves `${erpId}|${id}` que cada filtro aceita.
  const chavesSelecionadas = Object.fromEntries(
    FILTROS.map((f) => [
      f.key,
      new Set(
        (opcoes[f.key] || [])
          .filter((o) => filters[f.key]?.includes(o.id))
          .flatMap((o) => [...o.chaves]),
      ),
    ]),
  );

  // "Meus tickets": o usuário da HubSoft vinculado ao logado
  // (Administração › Usuários), pelo nome — vale nos tickets de todo ERP.
  const meuNome = paramsPorErp
    .find(({ erp: origem }) => origem.tipo === "HUBSOFT")
    ?.data?.userResponsaveis?.find((u) => u.id === userErpId)?.name;
  const meuId = userErpId && meuNome ? normalizar(meuNome) : null;
  const temMeusTickets = !!meuId;
  const meusTicketsAtivo =
    temMeusTickets &&
    filters.responsaveis?.length === 1 &&
    filters.responsaveis[0] === meuId;

  // ===== FILTRAGEM =====
  const termo = searchTerm.trim().toLowerCase();
  const matchesSearch = (ticket) =>
    !termo ||
    [
      ticket.protocolo,
      ticket.cliente?.nome,
      ticket.cliente?.codigo_cliente,
      ticket.descricao_abertura,
      ticket.servico?.id_cliente_servico,
    ].some((campo) => campo?.toString()?.toLowerCase()?.includes(termo));

  // `ignorar`: o painel de um filtro conta os tickets sem o próprio filtro,
  // senão só sobrariam os itens já selecionados.
  const filtrar = (ignorar) =>
    tickets.filter(
      (ticket) =>
        matchesSearch(ticket) &&
        FILTROS.every(
          (f) =>
            f.key === ignorar ||
            !filters[f.key]?.length ||
            f
              .valores(ticket)
              .some((v) =>
                chavesSelecionadas[f.key].has(chave(ticket.erpId, v)),
              ),
        ),
    );

  const filteredTickets = filtrar();
  const abertos = filteredTickets.filter((t) => !isResolvido(t));
  const resolvidos = filteredTickets.filter(isResolvido);
  const ticketsDaAba = { abertos, resolvidos, todos: filteredTickets }[aba];

  // Nome da etapa por `${erpId}|${id}` (as etapas são de cada ERP).
  const etapaPorChave = Object.fromEntries(
    paramsPorErp.flatMap(({ erp: origem, data }) =>
      (data?.etapasFluxoAtendimento || []).map((e) => [
        chave(origem.id, e.idEtapaFluxoAtendimento),
        e.etapaFluxoAtendimento,
      ]),
    ),
  );
  const nomeEtapa = (t) =>
    t.id_fluxo_atendimento_etapa != null
      ? etapaPorChave[chave(t.erpId, t.id_fluxo_atendimento_etapa)]
      : undefined;
  const corEtapa = (nome) => {
    const cor = fluxoAtendimentoColor[nome];
    return cor ? theme.palette.colors[cor]?.medium : undefined;
  };

  // ===== PAINÉIS (só tickets abertos) =====
  const contar = (lista, chaves) => {
    const mapa = new Map();
    lista.forEach((t) =>
      chaves(t).forEach(({ id, label }) => {
        const atual = mapa.get(id) || { id, label, value: 0 };
        atual.value += 1;
        mapa.set(id, atual);
      }),
    );
    return [...mapa.values()].sort((a, b) => b.value - a.value);
  };

  const rankingResponsaveis = contar(
    filtrar("responsaveis").filter((t) => !isResolvido(t)),
    (t) =>
      t.usuario_responsavel?.length
        ? t.usuario_responsavel.map((u) => ({
            id: normalizar(u.name),
            label: u.name,
          }))
        : [{ id: null, label: "Sem responsável" }],
  ).map((item) =>
    item.id == null
      ? { ...item, color: theme.palette.colors.gray.medium }
      : item,
  );

  const rankingEtapas = contar(
    filtrar("etapasFluxo").filter((t) => !isResolvido(t)),
    (t) => [
      nomeEtapa(t)
        ? { id: normalizar(nomeEtapa(t)), label: nomeEtapa(t) }
        : { id: null, label: "Sem etapa" },
    ],
  ).map((item) => ({ ...item, color: corEtapa(item.label) }));

  // ===== KPIs =====
  // Genéricos: só usam datas, responsáveis e aberto/resolvido, que todo ERP
  // tem (status como "Aguardando análise" são da HubSoft).
  const semResponsavel = abertos.filter(
    (t) => !t.usuario_responsavel?.length,
  ).length;
  const maisAntigo = abertos.reduce(
    (min, t) => (!min || t.data_cadastro < min ? t.data_cadastro : min),
    null,
  );
  const comFechamento = resolvidos.filter((t) => t.data_fechamento);
  const tempoMedioMin = comFechamento.length
    ? comFechamento.reduce(
        (soma, t) =>
          soma + dayjs(t.data_fechamento).diff(dayjs(t.data_cadastro), "minute"),
        0,
      ) / comFechamento.length
    : null;

  const cards = [
    {
      titulo: "Tickets no período",
      valor: filteredTickets.length,
      icon: 3,
      color: "speeData",
    },
    {
      titulo: "Abertos",
      valor: abertos.length,
      icon: 15,
      color: "inputData",
      helper: maisAntigo
        ? `Mais antigo: há ${duracao(maisAntigo)}`
        : "Nenhum aberto",
    },
    {
      titulo: "Abertos sem responsável",
      valor: semResponsavel,
      icon: 7,
      color: "statusDown",
    },
    {
      titulo: "Resolvidos",
      valor: resolvidos.length,
      icon: 14,
      color: "statusUp",
      helper:
        tempoMedioMin != null
          ? `Tempo médio: ${formatarMinutos(tempoMedioMin)}`
          : "Nenhum resolvido",
    },
  ];

  // ===== CHIPS DOS FILTROS ATIVOS =====
  const chipsAtivos = FILTROS.flatMap((f) =>
    (filters[f.key] || []).map((id) => {
      const opcao = (opcoes[f.key] || []).find((o) => o.id === id);
      return {
        key: `${f.key}-${id}`,
        label: `${f.chip}: ${opcao?.label || id}`,
        onDelete: () => toggleFiltro(f.key, id),
      };
    }),
  );
  const totalFiltrosAtivos = chipsAtivos.length;

  // ===== COLUNAS =====
  const columns = [
    {
      field: "protocolo",
      // Sem espaço para mais uma coluna: o ERP é a 2ª linha do protocolo, como
      // em "Cliente / Serviço".
      headerName: variosErps ? "Protocolo / ERP" : "Protocolo",
      // Larguras medidas: o protocolo da HubSoft (20 dígitos) cabe inteiro e,
      // a 1920px com os painéis ao lado (tabela ~1200px), não há rolagem.
      width: 176,
      renderCell: ({ row }) => (
        <Box sx={styles.cellStack}>
          <Typography
            variant="body2"
            fontWeight={700}
            color="primary.main"
            sx={styles.protocolo}
          >
            {row.protocolo}
          </Typography>
          {variosErps && <ErpChip erp={erpDoTicket(row)} sx={styles.seloErp} />}
        </Box>
      ),
    },
    {
      field: "data_cadastro",
      headerName: "Abertura",
      width: 125,
      valueGetter: (v) => (v ? new Date(v).getTime() : 0),
      renderCell: ({ row }) => (
        <Box sx={styles.cellStack}>
          <Typography variant="body2">
            {formatDate(row.data_cadastro)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.data_fechamento
              ? `Resolvido em ${duracao(row.data_cadastro, row.data_fechamento)}`
              : `Aberto há ${duracao(row.data_cadastro)}`}
          </Typography>
        </Box>
      ),
    },
    {
      field: "cliente",
      headerName: "Cliente / Serviço",
      flex: 1.2,
      minWidth: 200,
      valueGetter: (v) => v?.nome || "",
      renderCell: ({ row }) => (
        <Box sx={styles.cellStack}>
          {row.cliente?.clienteId ? (
            <Typography
              component={RouterLink}
              to={`/interno/home/perfil/servicos/${row.cliente.clienteId}`}
              onClick={(e) => e.stopPropagation()}
              variant="body2"
              noWrap
              title={nomeCliente(row)}
              sx={styles.linkCliente}
            >
              {nomeCliente(row)}
            </Typography>
          ) : (
            <Typography variant="body2" noWrap fontWeight={600}>
              {nomeCliente(row)}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.servico?.id_cliente_servico
              ? `${row.servico.id_cliente_servico} - ${row.servico.nome || ""}`
              : "Serviço não informado"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "tipoAtendimento",
      headerName: "Tipo / Descrição",
      flex: 1,
      minWidth: 170,
      valueGetter: (v) => v?.descricao || "",
      renderCell: ({ row }) => (
        <Box sx={styles.cellStack}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {row.tipoAtendimento?.descricao || "-"}
          </Typography>
          <Tooltip title={row.descricao_abertura || ""} arrow placement="top">
            <Typography variant="caption" color="text.secondary" noWrap>
              {row.descricao_abertura}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "id_fluxo_atendimento_etapa",
      headerName: "Etapa do fluxo",
      width: 160,
      valueGetter: (v, row) => nomeEtapa(row) || "",
      renderCell: ({ value }) =>
        value ? (
          <Box sx={styles.etapa}>
            <Box sx={{ ...styles.etapaDot, bgcolor: corEtapa(value) }} />
            <Typography variant="body2" noWrap title={value}>
              {value}
            </Typography>
          </Box>
        ) : (
          "-"
        ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      valueGetter: (v) => v?.descricao || "",
      renderCell: ({ row }) => <StatusChip prefixo={row.status?.prefixo} />,
    },
    {
      field: "usuario_responsavel",
      headerName: "Responsáveis",
      width: 100,
      sortable: false,
      renderCell: ({ value }) => (
        <Responsaveis usuarios={value} sx={styles.avatarGroup} />
      ),
    },
    {
      field: "acoes",
      headerName: "",
      width: 116,
      sortable: false,
      renderCell: ({ row }) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<VisibilityOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            handleOpen(row);
          }}
          sx={styles.botaoVerTicket}
        >
          Ver ticket
        </Button>
      ),
    },
  ];

  // ===== BARRA DE FILTROS =====
  const barraEsquerda = (
    <Box sx={{ ...styles.barraFiltros, width: "100%" }}>
      {/* Mesmo buscador do Fluxo de atendimento (6.0-suporte/atendimentos). */}
      <TextField
        size="small"
        label="Buscar ticket"
        autoComplete="off"
        placeholder="Protocolo, cliente, serviço ou descrição..."
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value.trimStart());
          setPageMobile(0);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlined />
            </InputAdornment>
          ),
          endAdornment: searchTerm ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                edge="end"
                onClick={() => {
                  setSearchTerm("");
                  setPageMobile(0);
                }}
              >
                <ClearOutlined fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={styles.busca}
      />
      {temMeusTickets && (
        <Tooltip title="Mostrar só os tickets em que sou responsável">
          <Chip
            icon={<PersonOutlined />}
            label="Meus tickets"
            color={meusTicketsAtivo ? "primary" : "default"}
            variant={meusTicketsAtivo ? "filled" : "outlined"}
            onClick={() =>
              setFiltro("responsaveis", meusTicketsAtivo ? [] : [meuId])
            }
            sx={styles.chipMeusTickets}
          />
        </Tooltip>
      )}
    </Box>
  );

  const barraDireita = (
    <Box sx={styles.barraFiltros}>
      <ButtonTime
        open={Boolean(anchorTempo)}
        anchorEl={anchorTempo}
        handleClick={(e) => setAnchorTempo(e.currentTarget)}
        handleClose={() => setAnchorTempo(null)}
        optionsTime={OPCOES_TEMPO}
        selectTime={filters.selectTime}
        handleSelectTime={handleSelectTime}
        filterData={{
          initialDate: filters.dataInicial,
          finalDate: filters.dataFinal,
        }}
        loading={loading}
        DateTime={DateTimePicker}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        handleStartChange={(v) => {
          setStartDate(v);
          if (v?.isAfter(endDate)) setEndDate(v);
        }}
        handleEndChange={setEndDate}
        handleAplicar={handleAplicarTempo}
        isValidRange={isValidRange}
        todayMaxDataTime={
          limiteFinal?.isBefore(dayjs()) ? limiteFinal : null
        }
      />
      <Badge badgeContent={totalFiltrosAtivos} color="primary">
        <Button
          variant="outlined"
          startIcon={<FilterAltOutlined />}
          onClick={() => setOpenFiltros(true)}
          sx={styles.botaoFiltros}
        >
          Filtros
        </Button>
      </Badge>
      <Tooltip title="Atualizar">
        <span>
          <Button
            size="small"
            variant="outlined"
            disabled={loading}
            onClick={buscar}
            sx={styles.buttonRefresh}
          >
            <Cached
              fontSize="small"
              sx={{ color: theme.palette.text.main }}
              className={loading ? "rotating" : ""}
            />
          </Button>
        </span>
      </Tooltip>
    </Box>
  );

  return (
      <Box>
        <FilterBar
          type={1}
          openFilter={openFilterBar}
          setOpenFilter={setOpenFilterBar}
          selectorComponentLeft={barraEsquerda}
          selectorComponentRight={barraDireita}
          actions={[
            {
              name: "Busca e período",
              icon: <DateRangeOutlined />,
              onClick: () => setOpenFilterBar(true),
            },
            {
              name: "Filtros",
              icon: <FilterAltOutlined />,
              onClick: () => setOpenFiltros(true),
            },
            { name: "Atualizar", icon: <Cached />, onClick: buscar },
          ]}
        />

        {/* FILTROS ATIVOS (sempre visíveis) */}
        <Box sx={styles.chipsAtivos}>
          <Chip
            size="small"
            icon={<DateRangeOutlined />}
            label={
              periodo
                ? `${periodo.dataInicial.format("DD/MM/YYYY HH:mm")} até ${periodo.dataFinal.format("DD/MM/YYYY HH:mm")}`
                : "Carregando período..."
            }
          />
          {chipsAtivos.map(({ key, ...chip }) => (
            <Chip key={key} size="small" color="primary" {...chip} />
          ))}
          {totalFiltrosAtivos > 0 && (
            <Button
              size="small"
              startIcon={<ClearAllOutlined />}
              onClick={handleClearFilters}
            >
              Limpar filtros
            </Button>
          )}
        </Box>

        <Grid container spacing={1}>
          {/* KPIs */}
          <Grid item xs={12}>
            {/* O card tem minWidth de 200px: 1, 2 ou 4 por linha. */}
            <Grid container spacing={1} columns={{ xs: 1, sm: 2, lg: 4 }}>
              {cards.map((card) => (
                <Grid item xs={1} key={card.titulo}>
                  <CardDataComponent
                    cardTile={card.titulo}
                    cardValue={card.valor}
                    typeIcon={card.icon}
                    colorCard={card.color}
                    helperText={card.helper}
                    loading={loading}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* TICKETS */}
          <Grid item xs={12} xxl={9} display="flex">
            <Paper
              variant={theme.palette.mode === "dark" ? "outlined" : "elevation"}
              elevation={theme.palette.mode === "dark" ? 0 : 3}
              sx={styles.paperPanel}
            >
              {loading && <LinearProgress sx={styles.loadingLinearProgress} />}
              <Box sx={styles.headerTickets}>
                <Tabs
                  value={aba}
                  onChange={(e, v) => {
                    setAba(v);
                    setPageMobile(0);
                  }}
                  variant="scrollable"
                  sx={styles.tabs}
                >
                  <Tab value="abertos" label={`Abertos (${abertos.length})`} />
                  <Tab
                    value="resolvidos"
                    label={`Resolvidos (${resolvidos.length})`}
                  />
                  <Tab
                    value="todos"
                    label={`Todos (${filteredTickets.length})`}
                  />
                </Tabs>
              </Box>
              <Divider />

              {isMobile ? (
                <Box sx={styles.mobileCardsContainer}>
                  {ticketsDaAba
                    .slice(
                      pageMobile * rowsPerPageMobile,
                      (pageMobile + 1) * rowsPerPageMobile,
                    )
                    .map((ticket) => (
                      <Paper
                        key={ticket.rowId}
                        variant="outlined"
                        sx={styles.mobileCard}
                        onClick={() => handleOpen(ticket)}
                      >
                        <Box sx={styles.mobileCardHeader}>
                          <Box>
                            <Typography
                              component="div"
                              variant="subtitle2"
                              fontWeight={700}
                              color="primary.main"
                            >
                              {ticket.protocolo}
                              {variosErps && (
                                <ErpChip
                                  erp={erpDoTicket(ticket)}
                                  sx={{ ...styles.seloErp, ml: 1 }}
                                />
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(ticket.data_cadastro)} ·{" "}
                              {ticket.data_fechamento
                                ? `resolvido em\u00a0${duracao(ticket.data_cadastro, ticket.data_fechamento)}`
                                : `aberto há\u00a0${duracao(ticket.data_cadastro)}`}
                            </Typography>
                          </Box>
                          <StatusChip prefixo={ticket.status?.prefixo} />
                        </Box>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {nomeCliente(ticket)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          noWrap
                        >
                          {ticket.tipoAtendimento?.descricao}
                          {nomeEtapa(ticket)
                            ? ` · ${nomeEtapa(ticket)}`
                            : ""}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={styles.mobileDescription}
                        >
                          {ticket.descricao_abertura}
                        </Typography>
                        <Box sx={styles.mobileCardFooter}>
                          <Responsaveis
                            usuarios={ticket.usuario_responsavel}
                            sx={styles.avatarGroup}
                          />
                        </Box>
                      </Paper>
                    ))}
                  {ticketsDaAba.length === 0 && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                      sx={{ py: 4 }}
                    >
                      Nenhum ticket encontrado.
                    </Typography>
                  )}
                  <TablePagination
                    component="div"
                    rowsPerPageOptions={[10, 25, 50]}
                    count={ticketsDaAba.length}
                    rowsPerPage={rowsPerPageMobile}
                    page={pageMobile}
                    onPageChange={(e, p) => setPageMobile(p)}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPageMobile(parseInt(e.target.value, 10));
                      setPageMobile(0);
                    }}
                    labelRowsPerPage="Por página:"
                  />
                </Box>
              ) : (
                <Box sx={styles.boxDatagrid}>
                  <DatagridComponent
                    rows={ticketsDaAba}
                    columns={columns}
                    isPagination
                    setSelectedRow={handleOpen}
                    sxDatagrid={styles.datagrid}
                    options={{
                      autoHeight: false,
                      getRowHeight: () => 52,
                      getRowId: (row) => row.rowId, // id se repete entre ERPs
                      // localeText substitui o pt-BR do tema: reaplica.
                      localeText: {
                        ...ptBR.components.MuiDataGrid.defaultProps.localeText,
                        noRowsLabel: "Nenhum ticket encontrado.",
                      },
                      initialState: {
                        sorting: {
                          sortModel: [{ field: "data_cadastro", sort: "desc" }],
                        },
                        pagination: { paginationModel: { pageSize: 25 } },
                      },
                    }}
                  />
                </Box>
              )}
            </Paper>
          </Grid>

          {/* VISÃO DO SUPERVISOR */}
          <Grid item xs={12} xxl={3} sx={{ position: "relative" }}>
            <Box sx={styles.boxPaineis}>
              <PainelRanking
                titulo="Abertos por responsável"
                itens={rankingResponsaveis}
                selecionados={filters.responsaveis || []}
                onToggle={(id) => toggleFiltro("responsaveis", id)}
                loading={loading}
              />
              <PainelRanking
                titulo="Abertos por etapa do fluxo"
                itens={rankingEtapas}
                selecionados={filters.etapasFluxo || []}
                onToggle={(id) => toggleFiltro("etapasFluxo", id)}
                loading={loading}
              />
            </Box>
          </Grid>
        </Grid>

        {/* DRAWER DE FILTROS */}
        <Drawer
          anchor="right"
          open={openFiltros}
          onClose={() => setOpenFiltros(false)}
        >
          <Box sx={styles.drawer}>
            <Box sx={styles.drawerHeader}>
              <Typography sx={styles.titlePanel}>Filtros</Typography>
              <IconButton onClick={() => setOpenFiltros(false)}>
                <KeyboardDoubleArrowRight />
              </IconButton>
            </Box>
            <Divider />
            <Box sx={styles.drawerBody}>
              <Typography variant="caption" color="text.secondary">
                Os filtros são aplicados na hora sobre os tickets do período.
              </Typography>
              {FILTROS.filter((f) => opcoes[f.key]).map((f) => {
                const Componente =
                  f.key === "clientes" ? VirtualizedAutocomplete : Autocomplete;
                const lista = opcoes[f.key] || [];
                return (
                  <Componente
                    key={f.key}
                    multiple
                    size="small"
                    disableCloseOnSelect
                    limitTags={3}
                    options={lista}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    {...(f.key === "clientes"
                      ? {
                          renderOption: (props, o) =>
                            renderOpcaoCliente()(props, o.original),
                        }
                      : {})}
                    value={lista.filter((o) => filters[f.key]?.includes(o.id))}
                    onChange={(e, novos) =>
                      setFiltro(
                        f.key,
                        novos.map((o) => o.id),
                      )
                    }
                    renderTags={(tags, getTagProps) =>
                      tags.map((o, index) => (
                        <Chip
                          size="small"
                          label={o.label}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={f.label}
                        placeholder="Selecionar"
                      />
                    )}
                  />
                );
              })}
            </Box>
            <Divider />
            <Box sx={styles.drawerFooter}>
              <Button
                startIcon={<ClearAllOutlined />}
                disabled={!totalFiltrosAtivos}
                onClick={handleClearFilters}
              >
                Limpar filtros
              </Button>
              <Button variant="contained" onClick={() => setOpenFiltros(false)}>
                Ver {filteredTickets.length}{" "}
                {filteredTickets.length === 1 ? "ticket" : "tickets"}
              </Button>
            </Box>
          </Box>
        </Drawer>

        {tipoAberto === "IXC" ? (
          <TicketModalContentIxc
            open={open}
            loading={loadingTicketDetails}
            handleClose={handleClose}
            erp={tipoAberto}
            erpId={ticketAberto.erpId || undefined}
            ticketDetails={ticketDetails}
            status={ticketDetails?.status}
            labelTabsErp={labelTabsErp}
            value={value}
            handleChange={handleChange}
            isEdit={true}
            isAtendimento={true}
            setTicketDetails={setTicketDetails}
            itensPublic={itensPublic}
            setItensPublic={setItensPublic}
            setSnackbar={setSnackbar}
            setEditedTicket={setEditedTicket}
            setIsEditTicket={setIsEditTicket}
            isEditTicket={isEditTicket}
            isEditOs={isEditOs}
            setIsEditOs={setIsEditOs}
            setIsNewOs={setIsNewOs}
            isNewOs={isNewOs}
          />
        ) : (
          <TicketModalContent
            open={open}
            loading={loadingTicketDetails}
            handleClose={handleClose}
            erp={tipoAberto}
            erpId={ticketAberto.erpId || undefined}
            ticketDetails={ticketDetails}
            labelTabsErp={labelTabsErp}
            value={value}
            handleChange={handleChange}
            isEdit={true}
            setTicketDetails={setTicketDetails}
            itensPublic={itensPublic}
            setItensPublic={setItensPublic}
            htmlForText={htmlForText}
            setSnackbar={setSnackbar}
            fluxoAtendimentoColor={fluxoAtendimentoColor}
            setEditedTicket={setEditedTicket}
            setIsEditTicket={setIsEditTicket}
            isEditTicket={isEditTicket}
            setIsFinishTicket={setIsFinishTicket}
            isFinishTicket={isFinishTicket}
            isEditOs={isEditOs}
            setIsEditOs={setIsEditOs}
            setIsNewOs={setIsNewOs}
            isNewOs={isNewOs}
          />
        )}
        <InternoModalConfirmacaoSaida
          openConfirmacao={confirmacaoSaidaPagina}
          confirmLoad={false}
          setOpenConfirmacao={setConfirmacaoSaidaPagina}
          execute={handleClose}
          cancel={handleCancelNavigation}
          severity={"warning"}
          title={"Atenção"}
          contentText={
            "As alterações não salvas serão perdidas, deseja continuar?"
          }
          headerText={"Você está prestes a sair do modo de edição."}
          confirmText={"Continuar"}
        />

        {!!snackbar && (
          <Snackbar
            open
            onClose={() => setSnackbar(null)}
            autoHideDuration={3000}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert {...snackbar} onClose={() => setSnackbar(null)} />
          </Snackbar>
        )}
      </Box>
  );
}
